import React, { FC } from "react";

import { toast } from "react-toastify";

import {
  useBoolean,
  useFooters,
  useApisMembersInsightPostBookmarkFoldersIndex,
  useCounter,
  useProvidersCurrentEmployee,
} from "~/hooks";

import {
  PostHeader,
  PostContent,
  InsightPostBookmarkMoveModal,
} from "~/components/organisms";

import {
  useApisInsightPostsLikesCreate,
  useApisInsightPostsLikesDestroy,
  useApisInsightPostsBookmarksCreate,
  useApisInsightPostsBookmarksDestroy,
  useApisInsightPostsSharesDestroy,
} from "../hooks/members";

import { InsightPostShareFormModal, InsightPostsLikedEmployeesModal } from "./";

import {
  InsightPostType,
  EmployeeRole,
  PostDetailMenuItemType,
  ResponseMessageType,
} from "~/domains";

import { PostDetailWrapper } from "~/ui";

type PropsType = {
  insightPost: InsightPostType;
  menuItems: PostDetailMenuItemType[];
  className?: string;
  isHighlight?: boolean;
  openComment: (value: string) => void;
};

export const InsightPostDetail: FC<PropsType> = ({
  insightPost,
  menuItems,
  className = "",
  isHighlight = false,
  openComment,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const {
    isChecked: isShareableModalOpen,
    setTrue: setShareableModalOpen,
    setFalse: setShareableModalClose,
  } = useBoolean(false);
  const {
    isChecked: isShared,
    setTrue: setShared,
    setFalse: setNotShared,
  } = useBoolean(insightPost.shared);
  const {
    isChecked: isBookmarkModalOpen,
    setTrue: setBookmarkModalOpen,
    setFalse: setBookmarkModalClose,
  } = useBoolean(false);
  const {
    isChecked: isLikedEmployeesModalOpen,
    setTrue: setLikedEmployeesModalOpen,
    setFalse: setLikedEmployeesModalClose,
  } = useBoolean(false);
  const {
    isChecked: liked,
    setTrue: setLiked,
    setFalse: setNotLiked,
  } = useBoolean(insightPost.liked);
  const {
    count: likeCount,
    increment: likeIncrement,
    decrement: likeDecrement,
  } = useCounter(insightPost.insightPostStat.likesCount);
  const {
    isChecked: bookmarked,
    setTrue: setBookmarked,
    setFalse: setNotBookmarked,
  } = useBoolean(insightPost.bookmarked);
  const {
    count: bookmarkCount,
    increment: bookmarkIncrement,
    decrement: bookmarkDecrement,
  } = useCounter(insightPost.insightPostStat.bookmarksCount);
  const { data: bookmarkFoldersIndex } =
    useApisMembersInsightPostBookmarkFoldersIndex({
      config: {
        enabled: isBookmarkModalOpen,
      },
    });

  const { mutate: shareDeleteRequest } = useApisInsightPostsSharesDestroy();

  const { mutate: likeCreateRequest } = useApisInsightPostsLikesCreate();
  const { mutate: likeDeleteRequest } = useApisInsightPostsLikesDestroy();

  const { mutate: bookmarkCreateRequest } =
    useApisInsightPostsBookmarksCreate();
  const { mutate: bookmarkDeleteRequest } =
    useApisInsightPostsBookmarksDestroy();

  const handleCreateLike = () => {
    likeCreateRequest(
      {
        insightPostId: insightPost.id,
      },
      {
        onSuccess: () => {
          setLiked();
          likeIncrement();
        },
      },
    );
  };

  const handleDeleteLike = () => {
    likeDeleteRequest(
      {
        insightPostId: insightPost.id,
      },
      {
        onSuccess: () => {
          setNotLiked();
          likeDecrement();
        },
      },
    );
  };

  const handleCreateBookmark = () => {
    bookmarkCreateRequest(
      {
        insightPostId: insightPost.id,
      },
      {
        onSuccess: () => {
          setBookmarked();
          bookmarkIncrement();
        },
      },
    );
  };

  const handleDeleteBookmark = () => {
    bookmarkDeleteRequest(
      {
        insightPostId: insightPost.id,
      },
      {
        onSuccess: () => {
          setNotBookmarked();
          bookmarkDecrement();
        },
      },
    );
  };

  const handleDeleteShare = () => {
    const isConfirm = window.confirm("シェアを解除しますか？");
    if (!isConfirm) return;

    shareDeleteRequest(
      {
        insightPostId: insightPost.id,
      },
      {
        onSuccess: (res) => {
          setNotShared();
          toast(res.message);
        },
      },
    );
  };

  const leftFooters = useFooters({
    like: {
      doneAlready: liked,
      undoOnClick: handleDeleteLike,
      doOnClick: handleCreateLike,
      count: likeCount,
      textOnClick: setLikedEmployeesModalOpen,
    },
    comment: {
      doneAlready: insightPost.commented,
      undoOnClick: () => openComment(insightPost.id),
      doOnClick: () => openComment(insightPost.id),
      count: insightPost.insightPostStat.commentsCount,
    },
    bookmark: {
      doneAlready: bookmarked,
      undoOnClick: handleDeleteBookmark,
      doOnClick: handleCreateBookmark,
      count: bookmarkCount,
    },
    aiBotComment: {
      doneAlready: insightPost.aiBotCommented,
      undoOnClick: () => openComment(insightPost.id),
      doOnClick: () => openComment(insightPost.id),
    },
    shareable: {
      doneAlready: isShared,
      undoOnClick: setShareableModalOpen,
      doOnClick: handleDeleteShare,
      disable:
        !insightPost.shareable ||
        currentEmployee?.employeeRole.id === EmployeeRole.MEMBER.id,
    },
  });

  const rightFooters = useFooters({
    bookmarkFolder: {
      doneAlready: bookmarked,
      undoOnClick: setBookmarkModalOpen,
      doOnClick: setBookmarkModalOpen,
      disable: !bookmarked,
    },
  });

  const shareOnSuccess = (data: ResponseMessageType) => {
    toast(data.message);
    setShareableModalClose();
    setShared();
  };

  const canBookmarkMove =
    isBookmarkModalOpen && bookmarked && bookmarkFoldersIndex;

  return (
    <PostDetailWrapper isHighlight={isHighlight} className={className}>
      {!insightPost.shareable && (
        <span className="block w-fit rounded text-sm font-semibold text-center pb-1 pt-0.5 px-3 text-white bg-icon-heart">
          シェア不可
        </span>
      )}
      <PostHeader
        miniText={insightPost.createdAt}
        postedEmployee={insightPost.employee}
        menuItems={menuItems}
        underLabel={`${insightPost.jobCategory.name} - ${
          insightPost.postType.name
        } -
        ${insightPost.jobCategoryPhase?.name || "すべて"}`}
        className={insightPost.shareable ? "" : "mt-4"}
      />
      <PostContent
        content={insightPost.content}
        images={insightPost.attachFiles}
        footers={leftFooters.footers}
        rightFooters={rightFooters.footers}
        blockQuote={
          insightPost.insightPostQuotePost
            ? {
                content: insightPost.insightPostQuotePost.content,
                employee: insightPost.insightPostQuotePost.employee,
                files: insightPost.insightPostQuotePost.attachFiles,
                createdAt: insightPost.insightPostQuotePost.createdAt,
              }
            : undefined
        }
        className="mt-1 pl-16"
      />
      {isShareableModalOpen && (
        <InsightPostShareFormModal
          originalInsightPost={insightPost}
          onClose={setShareableModalClose}
          onSuccess={shareOnSuccess}
        />
      )}
      {canBookmarkMove && (
        <InsightPostBookmarkMoveModal
          insightPostId={insightPost.id}
          onClose={setBookmarkModalClose}
        />
      )}
      {isLikedEmployeesModalOpen && (
        <InsightPostsLikedEmployeesModal
          insightPostId={insightPost.id}
          onClose={setLikedEmployeesModalClose}
        />
      )}
    </PostDetailWrapper>
  );
};
